import { computed, ref, type Ref } from 'vue'
import { message } from 'ant-design-vue'
import { batchToggleThirdParty } from '@/api/goodsSetting'

interface ThirdPartyBatchToggleOptions {
  selectedItems: Ref<goodsSetting.tableDataItem[]>
  prodChnl: Ref<string>
  clearSelection: () => void
  refresh: () => void | Promise<void>
}

interface ThirdPartyChannelConfig {
  payChnl: goodsSetting.ThirdPartyPayChannel
  requirePrice: boolean
  // 支持三方支付的商品类型；不配置则不限制
  supportedProdTypes?: string[]
}

const channelConfigs: Record<string, ThirdPartyChannelConfig> = {
  apple: {
    payChnl: 'enjoy_pay',
    requirePrice: true,
  },
  google: {
    payChnl: 'xsolla_pay',
    requirePrice: false,
    supportedProdTypes: ['10', '20'],
  },
}

const parseChannelPrices = (
  raw: goodsSetting.tableDataItem['channelPrices'],
): goodsSetting.ChannelPrices | undefined => {
  if (!raw) return undefined
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

const getThirdPartyPayInfo = (
  item: goodsSetting.tableDataItem,
  payChnl: goodsSetting.ThirdPartyPayChannel,
): goodsSetting.ThirdPartyPayInfo | undefined => parseChannelPrices(item.channelPrices)?.priceInfo?.[payChnl]

const isProdTypeSupported = (item: goodsSetting.tableDataItem, config: ThirdPartyChannelConfig): boolean =>
  !config.supportedProdTypes || config.supportedProdTypes.includes(String(item.prodType))

// 审核中的商品不允许开启/关闭三方支付
const APPROVAL_STATUS_IN_REVIEW = 10
// 变更中 / 不可修改的商品也不允许开启/关闭三方支付（与行内编辑、停用等操作对齐）
const REPLICA_STATUS_IN_PROGRESS = 1
const MOD_STATUS_LOCKED = 0
const isToggleAllowed = (item: goodsSetting.tableDataItem, config: ThirdPartyChannelConfig): boolean =>
  isProdTypeSupported(item, config) &&
  item.approvalStatus !== APPROVAL_STATUS_IN_REVIEW &&
  item.replicaStatus !== REPLICA_STATUS_IN_PROGRESS &&
  item.modStatus !== MOD_STATUS_LOCKED

export default function useThirdPartyBatchToggle(options: ThirdPartyBatchToggleOptions) {
  const toggleLoading = ref(false)
  const activeChannel = computed(() => channelConfigs[options.prodChnl.value])
  const showThirdPartyToggle = computed(() => !!activeChannel.value)

  const canEnableIds = computed(() => {
    const config = activeChannel.value
    if (!config) return []
    return options.selectedItems.value
      .filter((item) => {
        if (!isToggleAllowed(item, config)) return false
        const payInfo = getThirdPartyPayInfo(item, config.payChnl)
        if (payInfo?.enableThirdPay === true) return false
        return !config.requirePrice || !!payInfo?.prodPrice
      })
      .map((item) => item.id)
  })

  const canDisableIds = computed(() => {
    const config = activeChannel.value
    if (!config) return []
    return options.selectedItems.value
      .filter(
        (item) => isToggleAllowed(item, config) && getThirdPartyPayInfo(item, config.payChnl)?.enableThirdPay === true,
      )
      .map((item) => item.id)
  })

  const toggleThirdParty = async (enableThirdPay: boolean) => {
    const config = activeChannel.value
    if (!config || toggleLoading.value) return

    const ids = enableThirdPay ? canEnableIds.value : canDisableIds.value
    if (!ids.length) {
      message.info(enableThirdPay ? '勾选项中无可开启三方支付的商品' : '勾选项中无可关闭三方支付的商品')
      return
    }

    toggleLoading.value = true
    try {
      const productList = ids.map((id) => ({ id, payChnl: config.payChnl, enableThirdPay }))
      const res = await batchToggleThirdParty({
        prodChnl: options.prodChnl.value,
        productList,
      })
      if (!res || res.code !== '0') return

      message.success(config.payChnl === 'xsolla_pay' ? '操作成功，商品需重新提交审核后才会生效' : '操作成功')
      options.clearSelection()
      await options.refresh()
    } catch (error) {
      console.error('批量修改三方支付失败', error)
    } finally {
      toggleLoading.value = false
    }
  }

  return {
    showThirdPartyToggle,
    canEnableIds,
    canDisableIds,
    toggleLoading,
    toggleThirdParty,
  }
}
